import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Video, Zap, Wand2, Film, Play, Square, RectangleHorizontal, Clock, CreditCard, Loader2, CheckCircle2, XCircle, RefreshCw, Download, Trash2, Settings, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { BottomNav } from '../components/BottomNav';

const MODES = [
  { id: 'quick_reel', label: 'Quick Reel', icon: Zap, desc: 'Fast social-media product video', color: 'from-pink-500 to-rose-500' },
  { id: 'ai_motion_reel', label: 'AI Motion Reel', icon: Wand2, desc: 'Animate a product image', color: 'from-violet-500 to-purple-500' },
  { id: 'premium_ai_ad', label: 'Premium AI Ad', icon: Film, desc: 'Multi-scene advertisement', color: 'from-amber-500 to-orange-500' },
];

const ASPECTS = [
  { id: '9:16', label: '9:16', desc: 'Reel / Shorts', icon: RectangleHorizontal, rotate: true },
  { id: '1:1', label: '1:1', desc: 'Square', icon: Square },
  { id: '16:9', label: '16:9', desc: 'Landscape', icon: RectangleHorizontal },
];

const STATUS_LABELS = {
  queued: { label: 'Queued', color: 'text-slate-500', bg: 'bg-slate-100' },
  submitted: { label: 'Submitted', color: 'text-blue-600', bg: 'bg-blue-50' },
  processing: { label: 'Processing', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  completed: { label: 'Completed', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  failed: { label: 'Failed', color: 'text-red-600', bg: 'bg-red-50' },
  cancelled: { label: 'Cancelled', color: 'text-gray-500', bg: 'bg-gray-100' },
};

export const VideoStudioScreen = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [config, setConfig] = useState(null);
  const [credits, setCredits] = useState(null);
  const [mode, setMode] = useState('quick_reel');
  const [aspect, setAspect] = useState('9:16');
  const [duration, setDuration] = useState(5);
  const [prompt, setPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [activeGen, setActiveGen] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('create');

  useEffect(() => {
    loadProduct();
    loadConfig();
    loadCredits();
    loadHistory();
  }, [productId]);

  useEffect(() => {
    if (!activeGen || activeGen.status === 'completed' || activeGen.status === 'failed' || activeGen.status === 'cancelled') return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/video/generations/${activeGen.id}`);
        const gen = res.data.generation;
        setActiveGen(gen);
        if (gen.status === 'completed' || gen.status === 'failed' || gen.status === 'cancelled') {
          clearInterval(interval);
          loadCredits();
          loadHistory();
          if (gen.status === 'completed') toast.success('Video generated successfully!');
          if (gen.status === 'failed') toast.error(gen.error_message || 'Generation failed');
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [activeGen?.id, activeGen?.status]);

  const loadProduct = async () => {
    try {
      const res = await api.get(`/products/${productId}`);
      setProduct(res.data.product);
      const p = res.data.product;
      setPrompt(`${p.ai_title || p.name || 'Product'} — cinematic product showcase with elegant lighting`);
      if (p.images?.length > 0) setImageUrl(p.images[0].url || '');
    } catch { navigate('/my-products'); }
  };

  const loadConfig = async () => {
    try { const res = await api.get('/video/config'); setConfig(res.data); } catch {}
  };

  const loadCredits = async () => {
    try { const res = await api.get('/video/credits'); setCredits(res.data); } catch {}
  };

  const loadHistory = async () => {
    try {
      const res = await api.get(`/video/history?product_id=${productId}`);
      setHistory(res.data.generations || []);
    } catch {}
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Please enter a prompt'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/video/generate', {
        product_id: productId, mode, prompt: prompt.trim(),
        aspect_ratio: aspect, duration,
        image_url: mode === 'ai_motion_reel' && imageUrl ? imageUrl : undefined,
      });
      if (res.data.duplicate) {
        toast.info('Generation already in progress');
      } else {
        toast.success('Video generation started!');
      }
      setActiveGen({ id: res.data.generation_id, status: res.data.status });
      setTab('progress');
      loadCredits();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || 'Failed to start generation';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally { setGenerating(false); }
  };

  const handleCancel = async (genId) => {
    try {
      await api.post(`/video/generations/${genId}/cancel`);
      toast.success('Generation cancelled');
      loadHistory(); loadCredits();
      if (activeGen?.id === genId) setActiveGen(prev => ({ ...prev, status: 'cancelled' }));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Cancel failed'); }
  };

  const handleRetry = async (genId) => {
    try {
      const res = await api.post(`/video/generations/${genId}/retry`);
      setActiveGen({ id: res.data.generation_id, status: res.data.status });
      setTab('progress');
      loadCredits();
      toast.success('Retrying generation');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Retry failed'); }
  };

  const handleDelete = async (genId) => {
    try {
      await api.delete(`/video/generations/${genId}`);
      toast.success('Deleted');
      loadHistory();
    } catch {}
  };

  const estimatedCost = config?.credit_costs?.[mode]?.[config?.provider]?.[duration] || 1;

  if (!product) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="video-studio">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-1" data-testid="video-studio-back"><ChevronLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-slate-900 truncate">Video Studio</h1>
            <p className="text-xs text-slate-500 truncate">{product.ai_title || product.name}</p>
          </div>
          <div className="flex items-center gap-1.5 bg-emerald-50 px-2.5 py-1 rounded-full" data-testid="credit-balance">
            <CreditCard className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700">{credits?.balance ?? '—'}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-t border-slate-100">
          {[
            { id: 'create', label: 'Create' },
            { id: 'progress', label: 'Progress', badge: activeGen && !['completed','failed','cancelled'].includes(activeGen.status) },
            { id: 'history', label: 'History', count: history.length },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex-1 py-2.5 text-xs font-medium text-center relative ${tab === t.id ? 'text-indigo-600' : 'text-slate-500'}`}>
              {t.label}
              {t.count > 0 && <span className="ml-1 text-[10px] bg-slate-200 text-slate-600 px-1.5 rounded-full">{t.count}</span>}
              {t.badge && <span className="absolute top-1.5 right-1/4 w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse" />}
              {tab === t.id && <div className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-indigo-600 rounded-full" />}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4">
        {tab === 'create' && <CreateTab mode={mode} setMode={setMode} aspect={aspect} setAspect={setAspect}
          duration={duration} setDuration={setDuration} prompt={prompt} setPrompt={setPrompt}
          imageUrl={imageUrl} setImageUrl={setImageUrl} config={config} credits={credits}
          estimatedCost={estimatedCost} generating={generating} onGenerate={handleGenerate} />}
        {tab === 'progress' && <ProgressTab gen={activeGen} onCancel={handleCancel} onRetry={handleRetry} />}
        {tab === 'history' && <HistoryTab items={history} onRetry={handleRetry} onDelete={handleDelete} />}
      </div>
      <BottomNav />
    </div>
  );
};

const CreateTab = ({ mode, setMode, aspect, setAspect, duration, setDuration, prompt, setPrompt,
                     imageUrl, setImageUrl, config, credits, estimatedCost, generating, onGenerate }) => (
  <div className="space-y-5">
    {/* Mode Selection */}
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block">Generation Mode</label>
      <div className="grid gap-2">
        {MODES.map(m => (
          <button key={m.id} onClick={() => setMode(m.id)} data-testid={`mode-${m.id}`}
            className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${mode === m.id ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-200 bg-white'}`}>
            <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${m.color} flex items-center justify-center`}>
              <m.icon className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">{m.label}</div>
              <div className="text-xs text-slate-500">{m.desc}</div>
            </div>
            {mode === m.id && <CheckCircle2 className="w-5 h-5 text-indigo-500 shrink-0" />}
          </button>
        ))}
      </div>
    </div>

    {/* Aspect Ratio */}
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block">Aspect Ratio</label>
      <div className="flex gap-2">
        {ASPECTS.map(a => (
          <button key={a.id} onClick={() => setAspect(a.id)} data-testid={`aspect-${a.id.replace(':','-')}`}
            className={`flex-1 p-2.5 rounded-xl border-2 text-center transition-all ${aspect === a.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'}`}>
            <div className="text-sm font-bold text-slate-900">{a.label}</div>
            <div className="text-[10px] text-slate-500">{a.desc}</div>
          </button>
        ))}
      </div>
    </div>

    {/* Duration */}
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block">Duration</label>
      <div className="flex gap-2">
        {(config?.durations || [5, 10]).map(d => (
          <button key={d} onClick={() => setDuration(d)} data-testid={`duration-${d}`}
            className={`flex-1 flex items-center justify-center gap-1.5 p-2.5 rounded-xl border-2 transition-all ${duration === d ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'}`}>
            <Clock className="w-3.5 h-3.5" />
            <span className="text-sm font-medium">{d}s</span>
          </button>
        ))}
      </div>
    </div>

    {/* Prompt */}
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block">Video Prompt</label>
      <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3} data-testid="video-prompt"
        className="w-full p-3 rounded-xl border-2 border-slate-200 bg-white text-sm focus:border-indigo-500 focus:ring-0 outline-none resize-none"
        placeholder="Describe the video you want to create..." />
    </div>

    {/* Image URL (for AI Motion Reel) */}
    {mode === 'ai_motion_reel' && (
      <div>
        <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block">Product Image URL</label>
        <input value={imageUrl} onChange={e => setImageUrl(e.target.value)} data-testid="video-image-url"
          className="w-full p-3 rounded-xl border-2 border-slate-200 bg-white text-sm focus:border-indigo-500 focus:ring-0 outline-none"
          placeholder="https://..." />
      </div>
    )}

    {/* Cost & Generate */}
    <div className="bg-white rounded-xl border-2 border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500">Estimated Cost</div>
        <div className="flex items-center gap-1">
          <CreditCard className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-sm font-bold text-slate-900" data-testid="estimated-cost">{estimatedCost} credits</span>
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-slate-500">Your Balance</div>
        <span className={`text-sm font-semibold ${(credits?.balance || 0) >= estimatedCost ? 'text-emerald-600' : 'text-red-600'}`}>
          {credits?.balance ?? '—'} credits
        </span>
      </div>
      <button onClick={onGenerate} disabled={generating || (credits?.balance || 0) < estimatedCost}
        data-testid="generate-video-btn"
        className="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.98]">
        {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Video className="w-4 h-4" /> Generate Video</>}
      </button>
    </div>
  </div>
);

const ProgressTab = ({ gen, onCancel, onRetry }) => {
  if (!gen) return <div className="text-center py-12 text-slate-400 text-sm">No active generation</div>;
  const info = STATUS_LABELS[gen.status] || STATUS_LABELS.processing;
  const isTerminal = ['completed', 'failed', 'cancelled'].includes(gen.status);
  const progress = gen.status === 'queued' ? 10 : gen.status === 'submitted' ? 25 : gen.status === 'processing' ? 60 : 100;

  return (
    <div className="space-y-4" data-testid="generation-progress">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${info.bg} ${info.color}`} data-testid="gen-status">{info.label}</span>
          <span className="text-[10px] text-slate-400">{gen.id}</span>
        </div>

        {!isTerminal && (
          <div className="mb-4">
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all duration-1000" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
              <span className="text-xs text-slate-500">
                {gen.status === 'queued' && 'Waiting in queue...'}
                {gen.status === 'submitted' && 'Submitted to provider...'}
                {gen.status === 'processing' && 'AI is creating your video...'}
              </span>
            </div>
          </div>
        )}

        {gen.status === 'completed' && gen.persistent_asset_url && (
          <div className="mb-4">
            <video controls className="w-full rounded-lg bg-black" data-testid="generated-video">
              <source src={gen.persistent_asset_url} type="video/mp4" />
            </video>
          </div>
        )}

        {gen.status === 'failed' && gen.error_message && (
          <div className="mb-4 p-3 bg-red-50 rounded-lg">
            <p className="text-xs text-red-700" data-testid="gen-error">{gen.error_message}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mb-4">
          <div>Provider: <span className="font-medium text-slate-700">{gen.provider || '—'}</span></div>
          <div>Mode: <span className="font-medium text-slate-700">{gen.generation_type || '—'}</span></div>
          <div>Duration: <span className="font-medium text-slate-700">{gen.duration || '—'}s</span></div>
          <div>Ratio: <span className="font-medium text-slate-700">{gen.aspect_ratio || '—'}</span></div>
        </div>

        <div className="flex gap-2">
          {!isTerminal && (
            <button onClick={() => onCancel(gen.id)} data-testid="cancel-gen-btn"
              className="flex-1 py-2 rounded-lg border border-red-200 text-red-600 text-xs font-medium flex items-center justify-center gap-1">
              <XCircle className="w-3.5 h-3.5" /> Cancel
            </button>
          )}
          {(gen.status === 'failed' || gen.status === 'cancelled') && (
            <button onClick={() => onRetry(gen.id)} data-testid="retry-gen-btn"
              className="flex-1 py-2 rounded-lg bg-indigo-600 text-white text-xs font-medium flex items-center justify-center gap-1">
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          )}
          {gen.status === 'completed' && gen.persistent_asset_url && (
            <a href={gen.persistent_asset_url} target="_blank" rel="noopener noreferrer" data-testid="download-video-btn"
              className="flex-1 py-2 rounded-lg bg-emerald-600 text-white text-xs font-medium flex items-center justify-center gap-1">
              <Download className="w-3.5 h-3.5" /> Download
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const HistoryTab = ({ items, onRetry, onDelete }) => {
  if (!items.length) return <div className="text-center py-12 text-slate-400 text-sm">No video history yet</div>;
  return (
    <div className="space-y-3" data-testid="video-history">
      {items.map(gen => {
        const info = STATUS_LABELS[gen.status] || STATUS_LABELS.processing;
        return (
          <div key={gen.id} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${info.bg} ${info.color}`}>{info.label}</span>
                <p className="text-xs text-slate-700 mt-1 line-clamp-2">{gen.prompt}</p>
              </div>
              {gen.status === 'completed' && gen.persistent_asset_url && (
                <a href={gen.persistent_asset_url} target="_blank" rel="noopener noreferrer"
                  className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg shrink-0">
                  <Download className="w-4 h-4" />
                </a>
              )}
            </div>
            <div className="flex items-center gap-3 text-[10px] text-slate-400 mb-2">
              <span>{gen.provider}</span>
              <span>{gen.generation_type?.replace('_', ' ')}</span>
              <span>{gen.aspect_ratio}</span>
              <span>{gen.duration}s</span>
              <span>{gen.sellgen_credit_cost} cr</span>
            </div>
            <div className="flex gap-2">
              {(gen.status === 'failed' || gen.status === 'cancelled') && (
                <button onClick={() => onRetry(gen.id)}
                  className="text-[10px] font-medium text-indigo-600 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Retry
                </button>
              )}
              <button onClick={() => onDelete(gen.id)}
                className="text-[10px] font-medium text-red-400 flex items-center gap-1 ml-auto">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VideoStudioScreen;
